/**
 * API Client - Axios-based HTTP client with interceptors
 * Handles authentication, error handling, and request/response transformation
 */

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, ErrorResponse } from '@shared/types';

// Get environment variables
const API_URL = import.meta.env.VITE_API_URL || '';
const API_TIMEOUT = Number.parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10);

class ApiClient {
  private readonly client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;
  /** Deduplicates identical in-flight GET requests to prevent 429 bursts on page load */
  private readonly inflightGets = new Map<string, Promise<unknown>>();

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle token refresh and errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ErrorResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // Handle 401 Unauthorized - Try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearTokens();
            globalThis.dispatchEvent(new Event('auth:session-expired'));
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request — identical concurrent calls share a single in-flight request
   * to prevent 429 rate-limit bursts during page transitions.
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const key = params ? `${url}?${JSON.stringify(params)}` : url;
    const inflight = this.inflightGets.get(key) as Promise<T> | undefined;
    if (inflight) return inflight;

    const promise = this.client
      .get<ApiResponse<T>>(url, { params })
      .then((r) => this.handleResponse(r.data))
      .finally(() => this.inflightGets.delete(key));

    this.inflightGets.set(key, promise);
    return promise;
  }

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, data);
    return this.handleResponse(response.data);
  }

  /**
   * PUT request
   */
  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, data);
    if (response.status === 204) return undefined as T;
    return this.handleResponse(response.data);
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url);
    return this.handleResponse(response.data);
  }

  /**
   * PATCH request
   */
  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, data);
    return this.handleResponse(response.data);
  }

  /**
   * Handle API response and extract data
   */
  private handleResponse<T>(response: ApiResponse<T>): T {
    if (response.data === null) {
      throw new Error(response.message || 'No data received');
    }
    return response.data;
  }

  /**
   * Get access token from localStorage
   */
  private getAccessToken(): string | null {
    // Align with the rest of the app which stores the access token under 'authToken'
    return localStorage.getItem('authToken');
  }

  /**
   * Get refresh token from localStorage
   */
  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Store tokens in localStorage
   */
  setTokens(accessToken: string, refreshToken: string): void {
    // Keep token keys consistent across the app
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  /**
   * Clear tokens from localStorage
   */
  clearTokens(): void {
    // Remove both possible keys for safety, then standardize on 'authToken'
    localStorage.removeItem('authToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  /**
   * Refresh access token using refresh token
   */
  private async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await this.client.post<
          ApiResponse<{ accessToken: string; refreshToken: string }>
        >('/api/Authentication/refresh', { refreshToken });

        if (response.data.data) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          this.setTokens(accessToken, newRefreshToken);
          return accessToken;
        }

        throw new Error('Failed to refresh token');
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
